import React, { useState, useEffect } from 'react';
import { Brain, MessageSquare, ChevronLeft, ChevronRight, Heart, Maximize2, Minimize2, Bookmark } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { BibleNavigation } from './BibleNavigation';
import { bibleService } from '../services/bibleService';
import { AsyncBoundary } from './loading/AsyncBoundary';
import { LoadingFallback } from './loading/LoadingFallback';
import { useInsightsStore } from '../stores/insightsStore';
import { useFavoritesStore } from '../stores/favoritesStore';
import { useBookmarkStore } from '../stores/bookmarkStore';
import { useNotesStore } from '../stores/notesStore';
import { useAuthStore } from '../services/auth';
import { InsightModal } from './modals/InsightModal';
import { NoteModal } from './NoteModal';
import { AIAssistant } from './AIAssistant';
import { ReturnToLocationButton } from './ReturnToLocationButton';
import { VersionSelector } from './VersionSelector';
import { HelpButton } from './common/HelpButton';

interface BibleReaderProps {
  book?: string;
  chapter?: number;
  version: string;
  onBookChange: (book: string) => void;
  onChapterChange: (chapter: number) => void;
}

function BibleReaderContent({ book = 'Genesis', chapter = 1, version, onBookChange, onChapterChange }: BibleReaderProps) {
  const navigate = useNavigate();
  const { openInsightModal, generateInsights, setDetailedInsight } = useInsightsStore();
  const { toggleFavorite, isFavorite, loadFavorites } = useFavoritesStore();
  const { loadBookmark, updateBookmark, isLoading } = useBookmarkStore();
  const { getNotesForVerses } = useNotesStore();
  const { user, setShowAuthModal } = useAuthStore();
  const queryClient = useQueryClient();
  const readerRef = React.useRef<HTMLDivElement>(null);
  const location = useLocation();
  const { state } = location;

  // State declarations
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [verseNotesMap, setVerseNotesMap] = useState<Record<string, boolean>>({});
  const [verseFavoritesMap, setVerseFavoritesMap] = useState<Record<string, boolean>>({});
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [activeVerseId, setActiveVerseId] = useState<string | null>(null);
  const [selectedVerse, setSelectedVerse] = useState<{
    id: string;
    reference: string;
    book: string;
    chapter: number;
    verse: number;
    text: string;
    version: string;
  } | null>(null);

  // Format book name for display
  const displayBook = book.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  // Fetch passage data
  const { data: passage, isLoading: isPassageLoading, error, refetch } = useQuery({
    queryKey: ['bible-passage', version, displayBook, chapter],
    queryFn: async () => {
      try {
        const result = await bibleService.getPassage(version, `${displayBook} ${chapter}`);
        // Update the chapter if it was adjusted (e.g., defaulted to 1)
        if (result.chapter !== chapter) {
          onChapterChange(result.chapter);
        }
        return result;
      } catch (error) {
        console.error('Error in BibleReader queryFn:', error);
        throw error;
      }
    },
    staleTime: 0,
    gcTime: 1000 * 60 * 60 * 24 * 7, // 7 days
  });

  // Load favorites when component mounts
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Load bookmark on mount
  useEffect(() => {
    if (!isLoading) {
      loadBookmark();
    }
  }, []);

  // Update bookmark when chapter/book changes
  useEffect(() => {
    if (book && chapter && !isLoading) {
      updateBookmark(book, chapter);
    }
  }, [book, chapter]);

  // Force refetch when version changes
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['bible-passage', version] });
  }, [version, queryClient]);

  // Update URL when book or chapter changes
  useEffect(() => {
    const bookForUrl = book.toLowerCase().replace(/ /g, '-');
    navigate(`/read/${version}/${bookForUrl}/${chapter}`);
  }, [book, chapter, version, navigate]);

  // Scroll to verse if specified in state
  useEffect(() => {
    if (passage && state?.verse) {
      const verseElement = document.getElementById(`verse_${state.verse}`);
      if (verseElement) {
        verseElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        verseElement.classList.add('highlight-verse');
        setTimeout(() => {
          verseElement.classList.remove('highlight-verse');
        }, 2000);
      }
    }
  }, [state?.verse, passage]);

  // Enhanced fullscreen handling
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'f' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        toggleFullScreen();
      }
      if (e.key === 'Escape' && isFullScreen) {
        document.exitFullscreen().catch(err => console.error('Error exiting fullscreen:', err));
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('keydown', handleKeyPress);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [isFullScreen]);

  // Load notes for verses in the passage
  useEffect(() => {
    const loadNotesForPassage = async () => {
      if (passage?.verses) {
        try {
          // Format verse IDs to match the database format
          const verseIds = passage.verses.map(verse => 
            `${displayBook.toLowerCase()}_${chapter}_${verse.verse}`
          );
          const notes = await getNotesForVerses(verseIds);
          const notesMap: Record<string, boolean> = {};
          notes.forEach(note => {
            const [book, chap, verse] = note.verse_id.split('_');
            notesMap[`${book}_${chap}_${verse}`] = true;
          });
          setVerseNotesMap(notesMap);
        } catch (error) {
          console.error('Error loading notes:', error);
        }
      }
    };
    loadNotesForPassage();
  }, [passage?.verses, displayBook, chapter]);

  // Check favorite status for each verse
  useEffect(() => {
    const checkFavorites = async () => {
      if (passage?.verses) {
        try {
          const favoriteStatuses: Record<string, boolean> = {};
          for (const verse of passage.verses) {
            const verseId = `${displayBook.toLowerCase()}_${chapter}_${verse.verse}`;
            favoriteStatuses[`${displayBook}_${chapter}_${verse.verse}`] = await isFavorite(verseId);
          }
          setVerseFavoritesMap(favoriteStatuses);
        } catch (error) {
          console.error('Error checking favorites:', error);
        }
      }
    };
    checkFavorites();
  }, [passage?.verses, displayBook, chapter]);

  const navigateChapter = async (delta: number) => {
    try {
      const newChapter = chapter + delta;
      if (newChapter > 0) {
        onChapterChange(newChapter);
      }
    } catch (error) {
      console.error('Error navigating chapter:', error);
    }
  };

  const handleInsightClick = (verseInfo: { book: string; chapter: number; verse: number; text: string; version: string }) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    openInsightModal({
      ...verseInfo,
      book: verseInfo.book.toUpperCase()
    });
  };

  const handleFavoriteClick = async (verse: {
    id: string;
    book: string;
    chapter: number;
    verse: number;
    text: string;
    version: string;
  }) => {
    if (!user) {
      setShowAuthModal(true);
      return;
    }
    try {
      // Toggle favorite status
      await toggleFavorite(verse);
      
      // Update local state
      setVerseFavoritesMap(prev => ({
        ...prev,
        [verse.id]: !prev[verse.id]
      }));
    } catch (error) {
      console.error('Failed to update favorite:', error);
    }
  };

  const handleChapterInsightClick = () => {
    openInsightModal({
      book: displayBook,
      chapter,
      verse: 0, // 0 indicates chapter-level insight
      text: passage?.verses.map(v => v.text).join(' ') || '',
      version
    });
    setDetailedInsight(true);
    generateInsights();
  };

  const handleVerseClick = (verseId: string, reference: string) => {
    const [book, chap, verse] = verseId.split('_');
    setSelectedVerse({
      id: verseId,
      reference,
      book: displayBook,
      chapter: Number(chap),
      verse: Number(verse),
      text: passage?.verses.find(v => v.verse === Number(verse))?.text || '',
      version
    });
    setActiveVerseId(verseId);
    updateBookmark(book, Number(chap), Number(verse));
  };

  const toggleFullScreen = async () => {
    try {
      if (!document.fullscreenElement && readerRef.current) {
        await readerRef.current.requestFullscreen();
      } else if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (err) {
      console.error('Error toggling fullscreen:', err);
    }
  };

  if (isPassageLoading) {
    return <LoadingFallback />;
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-500">Error loading passage. Please try again.</p>
        <button onClick={() => refetch()} className="mt-4 btn-primary">
          Retry
        </button>
      </div>
    );
  }

  if (!passage) {
    return (
      <div className="text-center py-8">
        <p className="text-white/60">No passage found.</p>
      </div>
    );
  }

  return (
    <div 
      ref={readerRef} 
      className={`h-full flex flex-col ${isFullScreen ? 'fullscreen-container' : ''}`}
    >
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${isFullScreen ? 'py-4 h-full overflow-y-auto' : ''} flex-1`}>
        <div className="w-full">
          <div className="scripture-card">
            {/* Bible Navigation */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-grow">
                  <BibleNavigation
                    selectedBook={displayBook}
                    selectedChapter={chapter}
                    onSelectBook={onBookChange}
                    onSelectChapter={onChapterChange}
                  />
                </div>
                <div className="w-full sm:w-[180px]">
                  <VersionSelector
                    selectedVersion={version}
                    onSelectVersion={(newVersion) => {
                      queryClient.invalidateQueries({ queryKey: ['bible-passage'] });
                      navigate(`/read/${newVersion}/${book.toLowerCase()}/${chapter}`);
                    }}
                  />
                  <HelpButton pageName="bible-reader" className="ml-2" />
                </div>
              </div>
            </div>

            {/* Header with responsive spacing and font sizes */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-2">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gold-200">
                {displayBook} {chapter}
              </h1>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={toggleFullScreen}
                  className="btn-secondary"
                  aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
                >
                  {isFullScreen ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
                </button>
                <button
                  onClick={handleChapterInsightClick}
                  className="btn-secondary"
                  aria-label="Generate AI insights"
                >
                  <Brain size={20} />
                </button>
              </div>
            </div>

            {/* Verses with responsive text and spacing */}
            <div className="space-y-2">
              {passage.verses.map((verse) => {
                const verseId = `${book}_${chapter}_${verse.verse}`;
                const isActive = activeVerseId === verseId;
                return (
                  <div
                    key={verse.verse}
                    className="verse-container p-3 sm:p-4 hover:bg-white/5 transition-colors group relative"
                    onClick={() => handleVerseClick(verseId, `${displayBook} ${chapter}:${verse.verse}`)}
                  >
                    <div className="flex gap-2 sm:gap-4 items-start">
                      <span className="text-gold-500 font-medium min-w-[2rem]">{verse.verse}</span>
                      <div className="flex-grow relative">
                        <p className="text-base sm:text-lg">{verse.text}</p>
                        {/* Verse actions overlaying first line */}
                        <div className={`absolute right-0 top-0 flex flex-wrap gap-2 transition-opacity duration-200 bg-obsidian-900/80 backdrop-blur-sm rounded-lg ${isActive ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!user) {
                                setShowAuthModal(true);
                                return;
                              }
                              handleInsightClick({
                                book: displayBook,
                                chapter,
                                verse: verse.verse,
                                text: verse.text,
                                version
                              });
                            }}
                            className={`btn-secondary p-2 relative group ${!user ? 'opacity-40 hover:opacity-60' : ''}`}
                            aria-label="Get AI insights for verse"
                            title={!user ? "Sign in to use AI insights" : "Get AI insights"}
                          >
                            <Brain size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!user) {
                                setShowAuthModal(true);
                                return;
                              }
                              setSelectedVerse({
                                id: verseId,
                                reference: `${displayBook} ${chapter}:${verse.verse}`,
                                book: displayBook,
                                chapter,
                                verse: verse.verse,
                                text: verse.text,
                                version
                              });
                              setIsNoteModalOpen(true);
                            }}
                            className={`btn-secondary p-2 relative group ${!user ? 'opacity-40 hover:opacity-60' : ''}`}
                            aria-label="Add note"
                            title={!user ? "Sign in to add notes" : (verseNotesMap[verseId] ? "Edit note" : "Add note")}
                          >
                            <MessageSquare size={16} className={verseNotesMap[verseId] ? 'text-gold-400' : ''} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!user) {
                                setShowAuthModal(true);
                                return;
                              }
                              handleFavoriteClick({
                                id: verseId,
                                book: displayBook,
                                chapter,
                                verse: verse.verse,
                                text: verse.text,
                                version
                              });
                            }}
                            className={`btn-secondary p-2 relative group ${!user ? 'opacity-40 hover:opacity-60' : ''}`}
                            aria-label="Toggle favorite"
                            title={!user ? "Sign in to favorite verses" : (verseFavoritesMap[verseId] ? "Remove from favorites" : "Add to favorites")}
                          >
                            <Heart
                              size={16}
                              className={verseFavoritesMap[verseId] ? 'fill-gold-400 text-gold-400' : ''}
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Navigation buttons with improved mobile spacing */}
            <div className="flex justify-between mt-6 gap-4">
              <button
                onClick={() => onChapterChange(chapter - 1)}
                disabled={chapter <= 1}
                className="btn-primary flex items-center gap-2"
              >
                <ChevronLeft size={20} />
                <span className="hidden sm:inline">Previous</span>
              </button>
              <button
                onClick={() => onChapterChange(chapter + 1)}
                className="btn-primary flex items-center gap-2"
              >
                <span className="hidden sm:inline">Next</span>
                <ChevronRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {selectedVerse && isNoteModalOpen && (
        <NoteModal
          isOpen={isNoteModalOpen}
          onClose={() => {
            setIsNoteModalOpen(false);
            setSelectedVerse(null);
          }}
          verse={selectedVerse}
        />
      )}
      <InsightModal />
      <ReturnToLocationButton />
    </div>
  );
}

function BibleReader({ book, chapter, version, onBookChange, onChapterChange }: BibleReaderProps) {
  return (
    <AsyncBoundary fallback={<LoadingFallback />}>
      <BibleReaderContent
        book={book}
        chapter={chapter}
        version={version}
        onBookChange={onBookChange}
        onChapterChange={onChapterChange}
      />
    </AsyncBoundary>
  );
}

export default BibleReader;